import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import FileIcon from 'shared/components/FileIcon';
import DeleteButton from 'shared/components/buttons/Delete';
import ProgressBar from 'shared/components/ProgressBar';
import { File, Checkbox } from 'shared/components/Form';
import { ATTACHMENT_MAX_SIZE_MB, ATTACHMENT_TYPE } from 'shared/constants';
import styles from './FileUpload.module.scss';

const FileUpload = forwardRef(({
  type,
  file,
  fileError,
  showImportResume,
  importResume,
  handleChange,
  uploadProgress,
  removeFile,
  onAbortUpload,
  onFileSelect,
  buttonTitle,
  disabled
}, ref) => {
  const { t } = useTranslation('common');

  const getFileSize = (size) => {
    const kb = Math.round(size / 10.24) / 100;
    if (kb < 1000) {
      return `${kb} KB`;
    }
    const mb = Math.round(kb / 10.24) / 100;
    return `${mb} MB`;
  };

  return file ? (
    <>
      <div className="row">
        <div className="col-md-9 col-sm-12 col-xs-12">
          <div className={styles.file}>
            <FileIcon mime={file.type} />
            <div>
              {file.name}
            </div>
          </div>
          <small className={styles.size}>({ getFileSize(file.size) })</small>
          {showImportResume && type === ATTACHMENT_TYPE.CV && (
            <div className="pt8 ml32">
              <Checkbox
                fullWidth
                name="importResume"
                value={importResume}
                onChange={handleChange}
                label={t('userProfile.attachments.importResume')}
              />
            </div>
          )}
        </div>
        {(uploadProgress === 0 || uploadProgress >= 100) && (
          <div className={`col-md-3 col-sm-12 col-xs-12 ${styles.removeFile}`}>
            <DeleteButton disabled={disabled} onClick={removeFile}>{t('linkButton.delete')}</DeleteButton>
          </div>
        )}
      </div>
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="row">
          <div className="col-12">
            <ProgressBar withTitle value={uploadProgress} onAbort={onAbortUpload} />
          </div>
        </div>
      )}
    </>
  ) : (
    <div className="row">
      <div className={`col-12 ${styles.fileSelect}`}>
        <File
          ref={ref}
          disabled={disabled}
          error={fileError}
          title={buttonTitle || t('userProfile.attachments.selectFile')}
          iconClassName="la la-file-upload"
          onSelect={onFileSelect}
        >
          <small>{t('userProfile.attachments.maxSize', { mb: ATTACHMENT_MAX_SIZE_MB })}</small>
        </File>
      </div>
    </div>
  );
});

FileUpload.displayName = 'FileUpload';
export default FileUpload;
